import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Chip, IconButton,
  CircularProgress, Tooltip, Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import LockIcon from "@material-ui/icons/Lock";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { toast } from "react-toastify";
import { getBackendUrl } from "../../config";
import { getTicket, sendMessage, updateStatus } from "../../services/supportTicketService";

const backendUrl = getBackendUrl();

const PRIORITY_COLORS = {
  baixa: { bg: "#dcfce7", color: "#166534", label: "Baixa" },
  media: { bg: "#fef9c3", color: "#92400e", label: "Média" },
  alta: { bg: "#fed7aa", color: "#9a3412", label: "Alta" },
  urgente: { bg: "#fee2e2", color: "#991b1b", label: "Urgente" },
};

const STATUS_COLORS = {
  aberto: { bg: "#e0f2fe", color: "#0369a1", label: "Aberto" },
  em_atendimento: { bg: "#f3e8ff", color: "#7c3aed", label: "Em atendimento" },
  aguardando_resposta: { bg: "#fef9c3", color: "#92400e", label: "Aguardando resposta" },
  resolvido: { bg: "#dcfce7", color: "#166534", label: "Resolvido" },
  fechado: { bg: "#f1f5f9", color: "#64748b", label: "Fechado" },
};

const CATEGORY_LABELS = {
  tecnico: "Técnico", financeiro: "Financeiro", conta: "Conta",
  funcionalidade: "Funcionalidade", outro: "Outro",
};

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: theme.palette.primary.main,
    color: "#fff",
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleLeft: { display: "flex", flexDirection: "column", gap: 4 },
  titleText: { fontWeight: 700, fontSize: "1rem", color: "#fff" },
  chipRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  dialogContent: { padding: 0, display: "flex", flexDirection: "column", height: 480 },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  messageBubble: {
    maxWidth: "75%",
    borderRadius: 12,
    padding: "10px 14px",
    position: "relative",
  },
  bubbleAdmin: {
    background: theme.palette.primary.main,
    color: "#fff",
    alignSelf: "flex-start",
    borderBottomLeftRadius: 4,
  },
  bubbleCompany: {
    background: "#fff",
    color: "#1e293b",
    alignSelf: "flex-end",
    borderBottomRightRadius: 4,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  bubbleInternal: {
    background: "#fef9c3",
    color: "#92400e",
    alignSelf: "flex-start",
    borderBottomLeftRadius: 4,
    border: "1px solid #fde047",
  },
  senderName: { fontWeight: 700, fontSize: "0.72rem", marginBottom: 3, opacity: 0.75 },
  messageText: { fontSize: "0.88rem", lineHeight: 1.5, whiteSpace: "pre-wrap" },
  messageTime: { fontSize: "0.68rem", opacity: 0.55, marginTop: 4, textAlign: "right" },
  internalBadge: {
    display: "inline-flex", alignItems: "center", gap: 3,
    fontSize: "0.65rem", opacity: 0.7, marginTop: 3,
  },
  inputRow: {
    padding: "10px 16px 12px",
    background: "#fff",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: 8,
    alignItems: "flex-end",
  },
  inputField: { flex: 1 },
  closedBanner: {
    background: "#f1f5f9",
    textAlign: "center",
    padding: "10px 16px",
    color: "#64748b",
    fontSize: "0.85rem",
    borderTop: "1px solid #e2e8f0",
  },
  infoBar: {
    padding: "8px 20px",
    background: "#fff",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "0.8rem",
    color: "#64748b",
  },
  attachPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 16px",
    background: "#f1f5f9",
    borderTop: "1px solid #e2e8f0",
    fontSize: "0.78rem",
    color: "#475569",
  },
  attachPreviewImg: {
    maxHeight: 60,
    maxWidth: 80,
    borderRadius: 6,
    objectFit: "cover",
  },
  attachmentImg: {
    maxWidth: "100%",
    borderRadius: 8,
    marginTop: 6,
    cursor: "pointer",
    display: "block",
  },
  attachmentFile: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginTop: 6,
    padding: "4px 10px",
    background: "rgba(0,0,0,0.08)",
    borderRadius: 6,
    fontSize: "0.78rem",
    textDecoration: "none",
    color: "inherit",
  },
}));

const formatTime = (date) =>
  new Date(date).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const SupportTicketChat = ({ open, onClose, ticketId, isAdmin, onUpdated }) => {
  const classes = useStyles();
  const bottomRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [internalNote, setInternalNote] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [attachPreview, setAttachPreview] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await getTicket(ticketId);
      setTicket(data.ticket);
      setMessages(data.messages);
    } catch {
      toast.error("Erro ao carregar chamado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && ticketId) {
      load();
      setText("");
      setInternalNote(false);
      setAttachFile(null);
      setAttachPreview(null);
    }
  }, [open, ticketId]);

  const handleAttachChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setAttachFile(file);
    if (file.type.startsWith("image/")) {
      setAttachPreview(URL.createObjectURL(file));
    } else {
      setAttachPreview(null);
    }
    e.target.value = "";
  };

  const clearAttach = () => {
    setAttachFile(null);
    setAttachPreview(null);
  };

  useEffect(() => {
    if (messages.length > 0 && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() && !attachFile) return;
    setSending(true);
    try {
      const { data: msg } = await sendMessage(ticketId, {
        message: text.trim(),
        isInternal: internalNote,
        attachment: attachFile || undefined,
      });
      setMessages((prev) => [...prev, msg]);
      setText("");
      clearAttach();
      const { data } = await getTicket(ticketId);
      setTicket(data.ticket);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao enviar");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateStatus(ticketId, status);
      setTicket((prev) => ({ ...prev, status }));
      toast.success("Status atualizado");
      onUpdated && onUpdated();
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  const pri = ticket ? PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.media : null;
  const sta = ticket ? STATUS_COLORS[ticket.status] || STATUS_COLORS.aberto : null;
  const isClosed = ticket?.status === "fechado";
  const isResolved = ticket?.status === "resolvido";
  const inputBlocked = isClosed || (isResolved && !isAdmin);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <div className={classes.dialogTitle}>
        <div className={classes.titleLeft}>
          <Typography className={classes.titleText}>
            #{ticketId} — {ticket?.title || "Carregando..."}
          </Typography>
          {ticket && (
            <div className={classes.chipRow}>
              <span style={{ ...sta, padding: "1px 8px", borderRadius: 10, fontSize: "0.7rem", fontWeight: 700 }}>
                {sta?.label}
              </span>
              <span style={{ ...pri, padding: "1px 8px", borderRadius: 10, fontSize: "0.7rem", fontWeight: 700 }}>
                {pri?.label}
              </span>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", padding: "1px 8px", borderRadius: 10, fontSize: "0.7rem", fontWeight: 600 }}>
                {CATEGORY_LABELS[ticket.category] || ticket.category}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {isAdmin && ticket && !isClosed && (
            <>
              {ticket.status !== "resolvido" && (
                <Tooltip title="Marcar como resolvido">
                  <IconButton size="small" style={{ color: "#fff" }} onClick={() => handleStatusChange("resolvido")}>
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={isClosed ? "Fechado" : "Fechar chamado"}>
                <Button size="small" style={{ color: "#fff", textTransform: "none", fontSize: "0.72rem" }}
                  onClick={() => handleStatusChange("fechado")}>
                  Fechar
                </Button>
              </Tooltip>
            </>
          )}
          {!isAdmin && isResolved && !isClosed && (
            <Button size="small" style={{ color: "#fff", textTransform: "none", fontSize: "0.72rem" }}
              onClick={() => handleStatusChange("fechado")}>
              Fechar chamado
            </Button>
          )}
          <IconButton size="small" style={{ color: "#fff" }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {ticket && isAdmin && ticket.company && (
        <div className={classes.infoBar}>
          Empresa: <strong>{ticket.company?.name || `ID ${ticket.companyId}`}</strong>
          {" · "}Aberto em {formatTime(ticket.createdAt)}
        </div>
      )}

      <DialogContent className={classes.dialogContent}>
        <div className={classes.messagesArea}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <CircularProgress size={28} />
            </div>
          ) : (
            messages.map((msg) => {
              const fromAdmin = msg.senderCompanyId === 1;
              const bubbleClass = msg.isInternal
                ? classes.bubbleInternal
                : fromAdmin ? classes.bubbleAdmin : classes.bubbleCompany;

              const isImage = msg.attachmentUrl && /\.(jpe?g|png|gif|webp|bmp)$/i.test(msg.attachmentUrl);

              return (
                <div key={msg.id} className={`${classes.messageBubble} ${bubbleClass}`}>
                  <Typography className={classes.senderName}>{msg.senderName}</Typography>
                  {msg.message && (
                    <Typography className={classes.messageText}>{msg.message}</Typography>
                  )}
                  {msg.attachmentUrl && (
                    isImage ? (
                      <a href={`${backendUrl}/public/${msg.attachmentUrl}`} target="_blank" rel="noopener noreferrer">
                        <img
                          src={`${backendUrl}/public/${msg.attachmentUrl}`}
                          alt={msg.attachmentName || "anexo"}
                          className={classes.attachmentImg}
                        />
                      </a>
                    ) : (
                      <a
                        href={`${backendUrl}/public/${msg.attachmentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.attachmentFile}
                      >
                        <InsertDriveFileIcon style={{ fontSize: 16 }} />
                        {msg.attachmentName || "Arquivo"}
                      </a>
                    )
                  )}
                  {msg.isInternal && (
                    <span className={classes.internalBadge}>
                      <LockIcon style={{ fontSize: 10 }} /> nota interna
                    </span>
                  )}
                  <Typography className={classes.messageTime}>{formatTime(msg.createdAt)}</Typography>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {inputBlocked ? (
          <div className={classes.closedBanner}>
            {isResolved && !isAdmin
              ? "Este chamado foi resolvido. Para continuar, abra um novo chamado."
              : "Este chamado está fechado. Abra um novo chamado se precisar de mais ajuda."}
          </div>
        ) : (
          <>
            {attachFile && (
              <div className={classes.attachPreview}>
                {attachPreview ? (
                  <img src={attachPreview} alt="preview" className={classes.attachPreviewImg} />
                ) : (
                  <InsertDriveFileIcon style={{ fontSize: 20, color: "#64748b" }} />
                )}
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {attachFile.name}
                </span>
                <IconButton size="small" onClick={clearAttach}>
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>
            )}
            <div className={classes.inputRow}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: "none" }}
                onChange={handleAttachChange}
              />
              <Tooltip title="Anexar arquivo">
                <IconButton
                  size="small"
                  onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
                  style={{ color: attachFile ? "#7c3aed" : "#94a3b8" }}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <TextField
                className={classes.inputField}
                placeholder={internalNote ? "Nota interna (só visível para o admin)..." : "Digite sua mensagem..."}
                variant="outlined"
                size="small"
                multiline
                maxRows={4}
                value={text}
                onChange={(e) => { const v = e.target.value; setText(v); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              {isAdmin && (
                <Tooltip title={internalNote ? "Nota interna (ativo)" : "Enviar como nota interna"}>
                  <IconButton
                    size="small"
                    onClick={() => setInternalNote(!internalNote)}
                    style={{ color: internalNote ? "#92400e" : "#94a3b8" }}
                  >
                    <LockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                color="primary"
                onClick={handleSend}
                disabled={sending || (!text.trim() && !attachFile)}
              >
                {sending ? <CircularProgress size={18} /> : <SendIcon />}
              </IconButton>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupportTicketChat;
